import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Users, Loader2, ArrowLeft, ChevronDown, ChevronRight,
  TrendingUp, Shield, ChefHat, Store, UserCog,
  Plus, Zap, CircleDot,
} from 'lucide-react';
import Markdown from 'react-markdown';

const API = process.env.REACT_APP_BACKEND_URL;

const HEALTH_CONFIG = {
  green: { label: 'Healthy', color: 'bg-green-100 text-green-700 border-green-300', dot: 'bg-green-500' },
  amber: { label: 'Needs Attention', color: 'bg-amber-100 text-amber-700 border-amber-300', dot: 'bg-amber-500' },
  red: { label: 'Critical', color: 'bg-red-100 text-red-700 border-red-300', dot: 'bg-red-500' },
};

const SECTION_ICONS = {
  finance: TrendingUp,
  operations: Shield,
  kitchen: ChefHat,
  foh: Store,
  hr: UserCog,
};

const SECTION_COLORS = {
  finance: 'text-emerald-600',
  operations: 'text-blue-600',
  kitchen: 'text-orange-600',
  foh: 'text-purple-600',
  hr: 'text-rose-600',
};

export default function BoardMeetingPage() {
  const { token } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runPhase, setRunPhase] = useState('');
  const [depth, setDepth] = useState('brief');
  const [showRun, setShowRun] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchMeetings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/board-meetings`, { headers: { Authorization: `Bearer ${token}` } });
      setMeetings(await res.json());
    } catch { toast.error('Failed to load meetings'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchMeetings(); }, [fetchMeetings]);

  const fetchMeeting = async (id) => {
    try {
      const res = await fetch(`${API}/api/board-meetings/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedMeeting(await res.json());
    } catch { toast.error('Failed to load meeting'); }
  };

  const runMeeting = async () => {
    setRunning(true);
    setRunPhase('Gathering business data...');
    setShowRun(false);
    try {
      setRunPhase('Section heads are reporting...');
      const res = await fetch(`${API}/api/board-meeting/run`, {
        method: 'POST', headers, body: JSON.stringify({ depth }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to run meeting');
      }
      const meeting = await res.json();
      toast.success(`Board meeting complete (${meeting.credits_used} credits used)`);
      setSelectedMeeting(meeting);
      fetchMeetings();
    } catch (e) { toast.error(e.message); }
    finally { setRunning(false); setRunPhase(''); }
  };

  const createProjectFromRec = async (meetingId, rec) => {
    try {
      const res = await fetch(`${API}/api/board-meetings/${meetingId}/create-project`, {
        method: 'POST', headers, body: JSON.stringify(rec),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(`Project "${rec.title}" created`);
    } catch { toast.error('Failed to create project'); }
  };

  const toggleSection = (id) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // ─── Running State ───
  if (running) {
    return (
      <div className="flex flex-col items-center justify-center py-24 space-y-6" data-testid="meeting-running">
        <div className="relative">
          <div className="w-20 h-20 rounded-full border-4 border-primary/20 flex items-center justify-center">
            <Loader2 className="w-10 h-10 animate-spin text-primary" />
          </div>
        </div>
        <div className="text-center">
          <h2 className="text-xl font-bold mb-1">Board Meeting in Progress</h2>
          <p className="text-muted-foreground">{runPhase}</p>
          <p className="text-xs text-muted-foreground mt-2">This may take 30-60 seconds</p>
        </div>
      </div>
    );
  }

  // ─── Meeting Detail View ───
  if (selectedMeeting) {
    const m = selectedMeeting;
    const healthCfg = HEALTH_CONFIG[m.health] || HEALTH_CONFIG.amber;
    return (
      <div className="space-y-6" data-testid="meeting-detail">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setSelectedMeeting(null)} data-testid="back-to-meetings">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <span className="text-sm text-muted-foreground">
            {new Date(m.created_at).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
          <Badge variant="outline" className="text-xs">{m.depth === 'detailed' ? 'Detailed' : 'Brief'}</Badge>
          <Badge variant="outline" className="text-xs">{m.credits_used} credits</Badge>
        </div>

        {/* Health + CEO Summary */}
        <Card className="border-2" style={{ borderColor: m.health === 'green' ? '#86efac' : m.health === 'red' ? '#fca5a5' : '#fcd34d' }}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Mentor CEO Summary</CardTitle>
              <Badge className={healthCfg.color} data-testid="health-badge">
                <CircleDot className={`w-3 h-3 mr-1 ${healthCfg.dot.replace('bg-', 'text-')}`} />
                {healthCfg.label}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none" data-testid="ceo-report">
              <Markdown>{m.ceo_report}</Markdown>
            </div>
          </CardContent>
        </Card>

        {/* Recommended Projects */}
        {m.recommended_projects && m.recommended_projects.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Recommended Projects</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {m.recommended_projects.map((rec, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <div>
                    <p className="font-medium text-sm">{rec.title}</p>
                    {rec.reason && <p className="text-xs text-muted-foreground">{rec.reason}</p>}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => createProjectFromRec(m.id, rec)} data-testid={`create-project-${i}`}>
                    <Plus className="w-3 h-3 mr-1" /> Create
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Active Projects Progress */}
        {m.active_projects && m.active_projects.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Active Projects</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {m.active_projects.map((p) => (
                <div key={p.id} className="flex items-center gap-3">
                  <div className="flex-1">
                    <p className="text-sm font-medium">{p.title}</p>
                    <Progress value={p.progress_pct} className="h-1.5 mt-1" />
                  </div>
                  <span className="text-xs text-muted-foreground">{p.progress_pct}%</span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Section Reports */}
        <div className="space-y-3">
          <h3 className="font-semibold text-base">Section Reports</h3>
          {(m.section_reports || []).map((section) => {
            const Icon = SECTION_ICONS[section.id] || Users;
            const color = SECTION_COLORS[section.id] || 'text-gray-600';
            const expanded = expandedSections[section.id];
            return (
              <Card key={section.id} className="overflow-hidden" data-testid={`section-${section.id}`}>
                <button
                  className="w-full flex items-center justify-between p-4 hover:bg-accent/30 transition-colors"
                  onClick={() => toggleSection(section.id)}
                >
                  <div className="flex items-center gap-2">
                    <Icon className={`w-5 h-5 ${color}`} />
                    <span className="font-medium text-sm">{section.title}</span>
                  </div>
                  {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                </button>
                {expanded && (
                  <CardContent className="pt-0 pb-4">
                    <Separator className="mb-3" />
                    <div className="prose prose-sm max-w-none" data-testid={`section-report-${section.id}`}>
                      <Markdown>{section.report}</Markdown>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  // ─── Meetings List ───
  return (
    <div className="space-y-6" data-testid="board-meeting-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Board Meeting</h1>
          <p className="text-muted-foreground text-sm">AI-powered business review with section heads and CEO synthesis</p>
        </div>
        <Dialog open={showRun} onOpenChange={setShowRun}>
          <DialogTrigger asChild>
            <Button data-testid="run-meeting-btn">
              <Users className="w-4 h-4 mr-1" /> Run Meeting
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Run Board Meeting</DialogTitle>
              <DialogDescription>
                Run an AI board meeting across Finance, Operations, Kitchen, FOH & HR. Pick depth — Brief is quick, Detailed digs deeper.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium">Report Depth</label>
                <Select value={depth} onValueChange={setDepth}>
                  <SelectTrigger data-testid="depth-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="brief">Brief (~10 credits, 2-3 bullets per section)</SelectItem>
                    <SelectItem value="detailed">Detailed (~20 credits, full analysis)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="text-xs text-muted-foreground space-y-1">
                <p>5 section heads will analyse your business data:</p>
                <p>Finance Director, Operations Manager, Head Chef, FOH Manager, HR Director</p>
                <p>Then a Mentor CEO synthesises all reports into strategic recommendations.</p>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={runMeeting} data-testid="confirm-run-btn">
                <Zap className="w-4 h-4 mr-1" /> Run Board Meeting
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : meetings.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="font-medium mb-1">No board meetings yet</h3>
            <p className="text-sm text-muted-foreground mb-4 text-center max-w-md">
              Run your first AI board meeting to get strategic analysis from virtual section heads and a CEO mentor
            </p>
            <Button onClick={() => setShowRun(true)} data-testid="empty-run-btn">
              <Users className="w-4 h-4 mr-1" /> Run Your First Meeting
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {meetings.map((m) => {
            const healthCfg = HEALTH_CONFIG[m.health] || HEALTH_CONFIG.amber;
            return (
              <Card
                key={m.id}
                className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => fetchMeeting(m.id)}
                data-testid={`meeting-card-${m.id}`}
              >
                <CardContent className="flex items-center justify-between py-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${healthCfg.dot}`} />
                    <div>
                      <p className="font-medium text-sm">
                        {new Date(m.created_at).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
                      </p>
                      <p className="text-xs text-muted-foreground line-clamp-1">{(m.ceo_report || '').slice(0, 100)}...</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">{m.depth || 'brief'}</Badge>
                    <Badge variant="outline" className={`text-xs ${healthCfg.color}`}>{healthCfg.label}</Badge>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
